import ArrowLeftIcon from "@redotech/redo-icons/arrow-left.svg";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import Logo from "@redotech/redo-web/logo.svg";
import { RpcError } from "@redotech/rpc/errors";
import { useQueryClient } from "@tanstack/react-query";
import { memo, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { useRedoPartnersRpcClient } from "../rpc/client-context";
import { useAuthToken } from "./auth-token-context";
import { LoginHeroPanel } from "./login-hero-panel";
import * as loginCss from "./login.module.css";
import {
  messageForVerifyError,
  VERIFY_ERROR_FALLBACK,
} from "./verify-error-messages";

type State =
  | { kind: "idle" }
  | { kind: "verifying" }
  | { kind: "success" }
  | { kind: "error"; message: string };

export const VerifyPage = memo(function VerifyPage() {
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";
  const rpcClient = useRedoPartnersRpcClient();
  const queryClient = useQueryClient();
  const { setToken } = useAuthToken();
  const navigate = useNavigate();
  const [state, setState] = useState<State>({ kind: "idle" });

  // useRef-based StrictMode guard: React 18 dev double-mounts effects
  // and the magic link is single-use. Without the guard, the second
  // POST fails as "already used" and the user sees a confusing error
  // even though the first attempt succeeded.
  const startedRef = useRef(false);

  useEffect(() => {
    if (!token) {
      setState({ kind: "error", message: VERIFY_ERROR_FALLBACK });
      return;
    }
    if (startedRef.current) {
      return;
    }
    startedRef.current = true;
    setState({ kind: "verifying" });
    const controller = new AbortController();
    (async () => {
      try {
        const result = await rpcClient.verifyMagicLink(
          { token },
          { signal: controller.signal },
        );
        if (controller.signal.aborted) {
          return;
        }
        setToken(result.authToken);
        // Seed the `me` cache so AuthGuard sees the partner immediately
        // on the next render — saves a full RTT on the post-login
        // dashboard load. Key shape mirrors auth-data-context.tsx.
        queryClient.setQueryData(["me", result.authToken], result.partner);
        // Preload the dashboard chunk while the user is still seeing
        // the success transition. On RouterProvider navigation the
        // chunk is already in cache by the time the route resolves.
        void import("../dashboard");
        setState({ kind: "success" });
        navigate("/dashboard", { replace: true });
      } catch (err) {
        if (controller.signal.aborted) {
          return;
        }
        if (err instanceof RpcError) {
          setState({ kind: "error", message: messageForVerifyError(err.code) });
        } else {
          setState({ kind: "error", message: VERIFY_ERROR_FALLBACK });
        }
      }
    })();

    return () => {
      controller.abort();
    };
    // Effect runs once per mount; the token comes from the URL and
    // doesn't change without a full nav cycle.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className={loginCss.container}>
      <div className={loginCss.leftPanel}>
        <div className={loginCss.logoContainer}>
          <a
            className={loginCss.logoLockup}
            href="https://www.redo.com"
            rel="noreferrer"
            target="_blank"
          >
            <Logo className={loginCss.logo} />
            <span className={loginCss.logoSubmark}>Partners</span>
          </a>
        </div>
        <div className={loginCss.formWrapper}>
          <section className={loginCss.formArea}>
            {(state.kind === "idle" || state.kind === "verifying") && (
              <>
                <div className={loginCss.authIntro}>
                  <h1 className={loginCss.authTitle}>Signing you in</h1>
                  <p className={loginCss.authSubtitle}>
                    Verifying your login link...
                  </p>
                </div>
                <div className={loginCss.loadingContainer}>
                  <div className={loginCss.loadingSpinner} />
                </div>
              </>
            )}
            {state.kind === "success" && (
              <div className={loginCss.authIntro}>
                <h1 className={loginCss.authTitle}>Signed in</h1>
                <p className={loginCss.authSubtitle}>Redirecting...</p>
              </div>
            )}
            {state.kind === "error" && (
              <>
                <div className={loginCss.formContainer}>
                  <div className={loginCss.authIntro}>
                    <h1 className={loginCss.authTitle}>
                      We couldn't sign you in
                    </h1>
                    <p className={loginCss.authSubtitle}>{state.message}</p>
                  </div>
                </div>
                <div className={loginCss.stackedActions}>
                  <RedoButton
                    buttonClassName={loginCss.primaryActionButton}
                    className={loginCss.fullWidthButton}
                    hierarchy="primary"
                    onClick={() => navigate("/login", { replace: true })}
                    size="sm"
                    text="Request a new login link"
                    type="button"
                  />
                  <button
                    className={loginCss.secondaryLinkButton}
                    onClick={() => navigate("/login", { replace: true })}
                    type="button"
                  >
                    <ArrowLeftIcon className={loginCss.backArrowIcon} />
                    Back to sign in
                  </button>
                </div>
              </>
            )}
          </section>
        </div>
      </div>
      <LoginHeroPanel />
    </div>
  );
});
